// Downloads — in-app APK distribution.
//   • Every signed-in user sees the global "Tag My Assets" app + their own
//     company-branded APK (if the platform owner uploaded one).
//   • The platform owner (super-admin) sees ALL tenants' APKs and can
//     upload / delete builds, scoped global or to a specific company.
import React, { useState, useEffect, useCallback } from 'react';
import { MdSystemUpdate, MdDownload, MdDelete, MdCloudUpload, MdAndroid, MdPublic, MdBusiness, MdPictureAsPdf } from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { confirm } from '../lib/dialogs';
import SearchableSelect from '../components/SearchableSelect';

const fmtSize = (b) => {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const isDocItem = (it) => /pdf/i.test(it.content_type || '') || /\.pdf$/i.test(it.original_name || '');

const Toast = ({ msg, type, onClose }) => (
  <div className={`toast toast-${type}`} style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
    {msg}
    <button onClick={onClose} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 16 }}>×</button>
  </div>
);

const Downloads = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'admin' && !!user?.tenant?.is_platform_owner;

  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);        // id currently downloading
  const [toast, setToast] = useState(null);

  // Upload form (super-admin)
  const [file, setFile] = useState(null);
  const [scope, setScope] = useState('global');
  const [tenantId, setTenantId] = useState('');
  const [label, setLabel] = useState('');
  const [version, setVersion] = useState('');
  const [uploading, setUploading] = useState(false);

  const showToast = (msg, t = 'success') => { setToast({ msg, type: t }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/downloads');
      setItems(r.data || []);
    } catch { showToast('Failed to load downloads', 'error'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/system/tenants').then(r => setTenants(r.data || [])).catch(() => {});
  }, [isSuperAdmin]);

  const download = async (item) => {
    setBusy(item.id);
    try {
      const r = await api.get(`/downloads/${item.id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: item.content_type || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = item.original_name || `${item.tenant_slug || 'tagmyassets'}-${isDocItem(item) ? 'manual.pdf' : 'app.apk'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { showToast('Download failed', 'error'); }
    setBusy(null);
  };

  const doUpload = async (e) => {
    e.preventDefault();
    if (!file) { showToast('Choose a file first', 'error'); return; }
    if (scope === 'tenant' && !tenantId) { showToast('Pick a company', 'error'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('apk', file);
      fd.append('scope', scope);
      if (scope === 'tenant') fd.append('tenant_id', tenantId);
      if (label) fd.append('label', label);
      if (version) fd.append('version', version);
      await api.post('/downloads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast('Uploaded');
      setFile(null); setLabel(''); setVersion(''); setScope('global'); setTenantId('');
      e.target.reset?.();
      load();
    } catch (err) { showToast(err.response?.data?.error || 'Upload failed', 'error'); }
    setUploading(false);
  };

  const remove = async (item) => {
    if (!(await confirm(`Delete "${item.label}"? This removes it for everyone who can see it.`))) return;
    try { await api.delete(`/downloads/${item.id}`); showToast('Deleted'); load(); }
    catch { showToast('Delete failed', 'error'); }
  };

  return (
    <div className="page-container">
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <div>
          <h1 className="page-title"><MdSystemUpdate style={{ verticalAlign: -4, marginRight: 8 }} />Downloads</h1>
          <p className="page-subtitle">Install the Tag My Assets Android app and download resources like the user manual.</p>
        </div>
      </div>

      {/* Install hint */}
      <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--primary)' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <MdAndroid style={{ fontSize: 30, color: 'var(--primary)', flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Tap <strong>Download</strong>, then open the file to install. On first install Android asks to
            allow apps from this source — enable it, then reopen the APK. The app connects to this same server automatically.
          </div>
        </div>
      </div>

      {/* Upload (super-admin only) */}
      {isSuperAdmin && (
        <form onSubmit={doUpload} className="card" style={{ marginBottom: 20 }}>
          <div className="card-title" style={{ marginBottom: 14 }}><MdCloudUpload style={{ verticalAlign: -3, marginRight: 6 }} />Upload a file</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div>
              <label className="form-label">File (APK or PDF)</label>
              <input type="file" accept=".apk,.pdf,application/vnd.android.package-archive,application/pdf"
                onChange={e => setFile(e.target.files?.[0] || null)} />
            </div>
            <div>
              <label className="form-label">Availability</label>
              <SearchableSelect className="form-control" value={scope} onChange={e => setScope(e.target.value)}>
                <option value="global">Global — everyone</option>
                <option value="tenant">Specific company</option>
              </SearchableSelect>
            </div>
            {scope === 'tenant' && (
              <div>
                <label className="form-label">Company</label>
                <SearchableSelect className="form-control" value={tenantId} onChange={e => setTenantId(e.target.value)}>
                  <option value="">Select…</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
                </SearchableSelect>
              </div>
            )}
            <div>
              <label className="form-label">Label</label>
              <input className="form-control" placeholder="Tag My Assets (Android)" value={label} onChange={e => setLabel(e.target.value)} />
            </div>
            <div>
              <label className="form-label">Version</label>
              <input className="form-control" placeholder="2.3.2" value={version} onChange={e => setVersion(e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              <MdCloudUpload style={{ verticalAlign: -3, marginRight: 6 }} />{uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {loading ? (
        <div className="card">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
          No app packages available yet.{isSuperAdmin ? ' Upload one above.' : ' Ask your administrator.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {items.map(item => (
            <div key={item.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--primary), var(--accent))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isDocItem(item)
                    ? <MdPictureAsPdf style={{ color: '#fff', fontSize: 24 }} />
                    : <MdAndroid style={{ color: '#fff', fontSize: 24 }} />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>{item.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {item.version ? `v${item.version} · ` : ''}{fmtSize(item.size_bytes)}
                  </div>
                </div>
              </div>

              <div>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700,
                  padding: '3px 9px', borderRadius: 20,
                  background: item.scope === 'global' ? '#e8f5e9' : '#ede7f6',
                  color: item.scope === 'global' ? '#2e7d32' : '#5e35b1',
                }}>
                  {item.scope === 'global' ? <><MdPublic style={{ fontSize: 13 }} /> Global</> : <><MdBusiness style={{ fontSize: 13 }} /> {item.tenant_name || item.tenant_slug}</>}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy === item.id} onClick={() => download(item)}>
                  <MdDownload style={{ verticalAlign: -3, marginRight: 6 }} />{busy === item.id ? 'Preparing…' : 'Download'}
                </button>
                {item.can_manage && (
                  <button className="btn btn-outline" title="Delete" onClick={() => remove(item)}>
                    <MdDelete />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Downloads;
